/**
 * 教师端 - 我的U币页面
 * 
 * 功能:
 * 1. U币概览: 总U币余额、可用U币、已过期U币、U币构成
 * 2. U币明细: 单行紧凑布局、类型筛选、时间范围筛选、分页
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractBeijingTime, toLocalDateString } from '../../utils/timeUtils';
import { Crown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';

// ==================== 类型定义 ====================

interface PointAccount {
  id: string;
  user_id: string;
  role: string;
  purchase_rebate_points: number;
  error_correction_points: number;
  activity_reward_points: number;
  teacher_gift_points: number;
  total_points: number;
  daily_earned_points: number;
}

interface Transaction {
  id: string;
  user_id: string;
  role: string;
  points_change: number;
  point_type: string;
  source_type: string;
  source_id: string | null;
  source_detail: any;
  balance_after: number;
  expire_at: string | null;
  operator_id: string | null;
  operator_role: string | null;
  remark: string | null;
  created_at: string;
}

// ==================== 常量 ====================

// U币类型标签映射 - 统一为"支出"和"收入"
const POINT_TYPE_LABELS: Record<string, string> = {
  purchase_rebate: '收入',
  error_correction: '收入',
  activity_reward: '收入',
  teacher_gift: '收入',
  gift_deduction: '支出',
  deduction: '支出',
  expiration: '支出',
  refund_rollback: '收入',
  admin_adjust: '收入',
};

// U币类型颜色映射 - 收入用绿色,支出用红色
const POINT_TYPE_COLORS: Record<string, string> = {
  purchase_rebate: 'bg-green-100 text-green-700',
  error_correction: 'bg-green-100 text-green-700',
  activity_reward: 'bg-green-100 text-green-700',
  teacher_gift: 'bg-green-100 text-green-700',
  gift_deduction: 'bg-red-100 text-red-700',
  deduction: 'bg-red-100 text-red-700',
  expiration: 'bg-red-100 text-red-700',
  refund_rollback: 'bg-green-100 text-green-700',
  admin_adjust: 'bg-green-100 text-green-700',
};

const TIME_RANGES = [
  { label: '全部', value: 'all' },
  { label: '近7天', value: '7d' },
  { label: '近30天', value: '30d' },
  { label: '近90天', value: '90d' },
];

const getStartDate = (range: string): string | undefined => {
  if (range === 'all') return undefined;
  const days: Record<string, number> = { '7d': 7, '30d': 30, '90d': 90 };
  const d = new Date();
  d.setDate(d.getDate() - days[range]);
  return toLocalDateString(d);
};

// ==================== 辅助组件 ====================

const PointCategoryCard: React.FC<{
  title: string;
  points: number;
  color: string;
  icon: string;
}> = ({ title, points }) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
  };

  return (
    <div className={`rounded-xl p-3 border ${colorMap['blue'] || 'bg-gray-50 border-gray-200'}`}>
      <div className="text-xs text-gray-600 mb-1">{title}</div>
      <div className="text-lg font-bold text-gray-800">{points}</div>
    </div>
  );
};

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  size?: 'sm' | 'md';
}> = ({ label, active, onClick, size = 'md' }) => {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
        active
          ? 'bg-orange-500 text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      } ${size === 'sm' ? 'px-2 py-1 text-xs' : ''}`}
    >
      {label}
    </button>
  );
};

// 生成交易描述
const getTransactionDescription = (transaction: Transaction): string => {
  const { source_type, point_type, remark, points_change } = transaction;
  const typeLabel = POINT_TYPE_LABELS[point_type] || point_type;
  const isPositive = points_change > 0;
  const absPoints = Math.abs(points_change);

  if (remark) {
    if (source_type === 'admin_adjust') {
      return isPositive
        ? `管理员增加${typeLabel}${absPoints}分：${remark}`
        : `管理员扣减${typeLabel}${absPoints}分：${remark}`;
    }
    return remark;
  }

  switch (source_type) {
    case 'admin_adjust':
      return isPositive
        ? `管理员增加${typeLabel}${absPoints}分`
        : `管理员扣减${typeLabel}${absPoints}分`;
    case 'purchase_rebate':
      return `购课返利${absPoints}分`;
    case 'error_correction':
      return `纠错奖励${absPoints}分`;
    case 'order_deduction':
      return `U币抵扣订单${absPoints}分`;
    case 'refund_rollback':
      return `退款回滚${absPoints}分`;
    case 'expiration':
      return `U币过期清零${absPoints}分`;
    default:
      return isPositive
        ? `增加${typeLabel}${absPoints}分`
        : `扣减${typeLabel}${absPoints}分`;
  }
};

// 流水项组件
const TransactionItem: React.FC<{
  transaction: Transaction;
  formatDate: (date: string) => string;
}> = ({ transaction, formatDate }) => {
  const isPositive = transaction.points_change > 0;
  const description = getTransactionDescription(transaction);

  return (
    <div className="bg-white rounded-xl px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span
          className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
            POINT_TYPE_COLORS[transaction.point_type] || 'bg-gray-100 text-gray-700'
          }`}
        >
          {POINT_TYPE_LABELS[transaction.point_type] || transaction.point_type}
        </span>
        <span className="text-sm text-gray-700 truncate">
          {description}
        </span>
        <div className={`text-sm font-bold whitespace-nowrap ml-auto ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {isPositive ? '+' : ''}{transaction.points_change}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-1">
        <div className="text-xs text-gray-400 whitespace-nowrap">
          {formatDate(transaction.created_at)}
        </div>
        <div className="text-xs text-gray-400">
          余额: {transaction.balance_after}
        </div>
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

const TeacherGiftPoints: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);

  // U币概览相关状态
  const [account, setAccount] = useState<PointAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accountLoading, setAccountLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<string>('all');
  const pageSize = 20;

  // 初始化：获取教师 auth ID 和 session token
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUserId(user.id);

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      setAccessToken(token);

      // 数据加载
      fetchAccount(user.id, token);
      fetchTransactions(user.id, token);
    };
    init();
  }, []);

  // 筛选变化时获取流水
  useEffect(() => {
    if (userId && accessToken) {
      fetchTransactions(userId, accessToken);
    }
  }, [page, filterType, timeRange]);

  // ==================== U币概览数据获取 ====================

  const fetchAccount = async (uid: string, token?: string) => {
    try {
      setAccountLoading(true);
      const { data: existingAccount } = await supabase
        .from('user_point_accounts')
        .select('*')
        .eq('user_id', uid)
        .eq('role', 'teacher')
        .maybeSingle();

      if (existingAccount) {
        setAccount(existingAccount);
      } else {
        // 创建教师U币账户
        const authToken = token || accessToken;
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            action: 'create_account',
            user_id: uid,
            role: 'teacher',
          }),
        });
        const result = await response.json();
        if (result.success) {
          setAccount(result.account);
        }
      }
    } catch (error) {
      console.error('Failed to fetch account:', error);
    } finally {
      setAccountLoading(false);
    }
  };

  const fetchTransactions = async (uid: string, token?: string) => {
    try {
      setTransactionsLoading(true);

      const body: any = {
        action: 'get_transactions',
        user_id: uid,
        role: 'teacher',
        page,
        page_size: pageSize,
      };

      // 根据筛选条件设置 point_types
      if (filterType === 'income') {
        // 收入类型
        body.point_types = ['purchase_rebate', 'error_correction', 'activity_reward', 'teacher_gift', 'refund_rollback', 'admin_adjust'];
      } else if (filterType === 'expense') {
        // 支出类型
        body.point_types = ['gift_deduction', 'deduction', 'expiration'];
      }

      const startDate = getStartDate(timeRange);
      if (startDate) {
        body.start_date = startDate;
      }

      const authToken = token || accessToken;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (result.success) {
        setTransactions(result.transactions || []);
        setTotalPages(result.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const bj = extractBeijingTime(dateStr);
    if (!bj) return '-';
    return `${bj.year}-${bj.month}-${bj.day} ${bj.hour}:${bj.minute}`;
  };

  // 已过期U币 = 总U币 - 各类可用U币之和
  const availablePoints = (account?.purchase_rebate_points || 0) +
    (account?.error_correction_points || 0) +
    (account?.activity_reward_points || 0) +
    (account?.teacher_gift_points || 0);
  const expiredPoints = (account?.total_points || 0) - availablePoints;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Crown className="w-5 h-5 text-orange-500" />
          <h1 className="text-xl font-bold text-gray-800">我的U币</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* 总U币余额卡片 */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-center mb-4">
            <div className="text-sm opacity-90 mb-1">我的总U币</div>
            <div className="text-4xl font-bold">{account?.total_points || 0}</div>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm">
            <div>
              <span className="opacity-75">可用U币</span>
              <span className="font-bold ml-2">{availablePoints}</span>
            </div>
            {expiredPoints > 0 && (
              <div>
                <span className="opacity-75">已过期</span>
                <span className="font-bold ml-2">{expiredPoints}</span>
              </div>
            )}
          </div>
        </div>

        {/* U币构成卡片 */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">U币构成</h2>
          <div className="grid grid-cols-2 gap-3">
            <PointCategoryCard
              title="购课返利"
              points={account?.purchase_rebate_points || 0}
              color="blue"
              icon="rebate"
            />
            <PointCategoryCard
              title="纠错奖励"
              points={account?.error_correction_points || 0}
              color="green"
              icon="correction"
            />
          </div>
        </div>

        {/* 筛选器 - 简化为全部、收入、支出 */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
          {/* 收支类型筛选 */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <FilterChip label="全部" active={filterType === 'all'} onClick={() => { setFilterType('all'); setPage(1); }} />
            <FilterChip label="收入" active={filterType === 'income'} onClick={() => { setFilterType('income'); setPage(1); }} />
            <FilterChip label="支出" active={filterType === 'expense'} onClick={() => { setFilterType('expense'); setPage(1); }} />
          </div>
          {/* 时间范围筛选 */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {TIME_RANGES.map((range) => (
              <FilterChip
                key={range.value}
                label={range.label}
                active={timeRange === range.value}
                onClick={() => { setTimeRange(range.value); setPage(1); }}
                size="sm"
              />
            ))}
          </div>
        </div>

        {/* U币明细列表 */}
        {transactionsLoading && transactions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
            加载中...
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">
              <Crown className="w-12 h-12 mx-auto" />
            </div>
            <div className="text-gray-500">暂无U币流水记录</div>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => (
              <TransactionItem key={tx.id} transaction={tx} formatDate={formatDate} />
            ))}
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              上一页
            </button>
            <span className="text-sm text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
            >
              下一页
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherGiftPoints;
